/**
 * audioCategories — pure helpers for the live-session wizard's audio picker
 * (CORE-804).
 *
 * Two jobs, both stateless and render-free so they can be unit-tested:
 *  1. Map a SoundTrack `category` (a free-form BE string) to a canonical
 *     SoundTrackCategory and to its localized i18n key. The raw values are
 *     technical English (`BinauralBeats`, `TibetanBowls`) and must never reach
 *     a Russian UI — that leak is exactly what CORE-753 / CORE-789 fixed.
 *  2. Decide whether the «показано N из M» truncation hint should show. A list
 *     that drops everything past `limit` without a total can't tell «это всё»
 *     from «первые N» — that indistinguishability is the CORE-804 defect.
 */
import type { SoundTrackCategory } from '../../../types/auth';

// Canonical order for the filter chips. Mirrors SoundTrackCategory in
// types/auth.ts — the 7 values the BE assigns to a track.
export const SOUND_TRACK_CATEGORIES: readonly SoundTrackCategory[] = [
  'Ambient',
  'BinauralBeats',
  'NatureSounds',
  'Mantra',
  'TibetanBowls',
  'WhiteNoise',
  'Other',
] as const;

const CANONICAL = new Set<string>(SOUND_TRACK_CATEGORIES);

/** Normalise a free-form `category` string to a canonical SoundTrackCategory.
 *  The BE column is a free string; anything outside the 7 known values is
 *  bucketed as 'Other', so the filter never sprouts a stray English chip. */
export function normalizeAudioCategory(category: string | null | undefined): SoundTrackCategory {
  return category && CANONICAL.has(category) ? (category as SoundTrackCategory) : 'Other';
}

/** i18n key (within the `lives` namespace) for a category's localized label.
 *  Unknown / free-form values fall back to the 'Other' label. */
export function audioCategoryLabelKey(category: string | null | undefined): string {
  return `wizard.audioCategory.${normalizeAudioCategory(category)}`;
}

/** Categories actually present in a loaded list, in canonical order. Rendering
 *  only these avoids showing 7 dead filters over a library that has two. */
export function availableAudioCategories(items: { category: string }[]): SoundTrackCategory[] {
  const present = new Set(items.map((i) => normalizeAudioCategory(i.category)));
  return SOUND_TRACK_CATEGORIES.filter((c) => present.has(c));
}

/** Category label for a library-picker row (CORE-804 QA return #3). The
 *  in-room «Сменить медиа» picker rendered `{category}` verbatim, leaking raw
 *  audio enums (`BinauralBeats`, `TibetanBowls`) into the Russian UI — the same
 *  leak CORE-753 / CORE-789 fixed. Audio rows resolve to an i18n key (feed it
 *  through `t()`); video categories are free-form strings with no canonical
 *  list whose values are already user-facing labels, so they stay verbatim
 *  (`i18nKey: null`, show `raw`). */
export interface RowCategoryLabel {
  /** i18n key within the `lives` namespace, or `null` for a verbatim string. */
  i18nKey: string | null;
  /** The original category string; the fallback shown when `i18nKey` is null. */
  raw: string;
}

export function resolveRowCategoryLabel(
  kind: 'audio' | 'video',
  category: string,
): RowCategoryLabel {
  return kind === 'audio'
    ? { i18nKey: audioCategoryLabelKey(category), raw: category }
    : { i18nKey: null, raw: category };
}

/** Filter items by a selected category; `null` = show all. */
export function filterByAudioCategory<T extends { category: string }>(
  items: T[],
  category: SoundTrackCategory | null,
): T[] {
  return category ? items.filter((i) => normalizeAudioCategory(i.category) === category) : items;
}

/** Empty-state shown by a library picker after loading finishes.
 *
 * The source library and its filtered projection answer different questions:
 * an empty source means there is no content to choose, while an empty
 * projection means the organizer's search/filter has no matches. CORE-804's
 * search originally checked only the projection and therefore told users that
 * a populated library was empty. */
export type LibraryEmptyState = 'library-empty' | 'no-results' | null;

export function resolveLibraryEmptyState(
  loaded: number,
  shown: number,
): LibraryEmptyState {
  if (shown > 0) return null;
  return loaded > 0 ? 'no-results' : 'library-empty';
}

/** Whether to show the «показано N из M» truncation hint: only when the server
 *  holds more than we loaded. Guards against a bogus total (NaN / negative). */
export function isLibraryTruncated(loaded: number, total: number): boolean {
  return Number.isFinite(total) && total > loaded;
}

// ─────────────────── pagination (CORE-804 QA return) ───────────────────
//
// The `/api/soundtracks` (and `/api/videos`) endpoint silently clamps `limit`
// to 100 server-side: asking for 200 still returns 100 of a 134-track library,
// so 34 tracks were unpickable and «показано N из M» read «100 из 134» forever.
// Raising LIBRARY_LIMIT does nothing — the ceiling is the server's. The fix is
// to walk the library by `offset` until the whole thing is loaded, capped so a
// growing library can't turn the wizard into an unbounded download (past the
// cap the truncation hint reappears and search takes over).

/** Server-enforced page size. Requesting more than this is clamped anyway. */
export const LIBRARY_PAGE_SIZE = 100;
/** Stop paging past this many items; beyond it the «N из M» hint + search
 *  carry the tail instead of an ever-growing fetch. */
export const LIBRARY_MAX_ITEMS = 400;

/** Offsets to request to load a library of `total` items, page 0 first, up to
 *  `cap`. Always yields at least `[0]` so the first page (which reveals the
 *  real total) is fetched even when `total` is unknown/zero. */
export function planLibraryOffsets(
  total: number,
  pageSize: number = LIBRARY_PAGE_SIZE,
  cap: number = LIBRARY_MAX_ITEMS,
): number[] {
  const size = pageSize > 0 ? pageSize : LIBRARY_PAGE_SIZE;
  const target = Math.min(Math.max(0, Number.isFinite(total) ? total : 0), cap);
  const offsets: number[] = [];
  for (let o = 0; o < target; o += size) offsets.push(o);
  if (offsets.length === 0) offsets.push(0);
  return offsets;
}

/** Walk a paginated library to completion (bounded by LIBRARY_MAX_ITEMS).
 *  `fetchPage(limit, offset)` is injected so this stays render/network-free and
 *  unit-testable. Page 0 reveals the total; the rest are fetched by `offset`.
 *  Stops early if a page comes back short (server ran out of rows). Returns the
 *  concatenated items and the server's reported `total`. */
export async function collectLibraryPages<T>(
  fetchPage: (limit: number, offset: number) => Promise<{ items: T[]; totalCount: number }>,
): Promise<{ items: T[]; total: number }> {
  const first = await fetchPage(LIBRARY_PAGE_SIZE, 0);
  const total = Number.isFinite(first.totalCount) ? first.totalCount : first.items.length;
  const items: T[] = [...first.items];
  const rest = planLibraryOffsets(total, LIBRARY_PAGE_SIZE, LIBRARY_MAX_ITEMS).filter((o) => o > 0);
  for (const offset of rest) {
    if (first.items.length < LIBRARY_PAGE_SIZE) break; // first page already short → no more rows
    const page = await fetchPage(LIBRARY_PAGE_SIZE, offset);
    items.push(...page.items);
    if (page.items.length < LIBRARY_PAGE_SIZE) break; // server ran out early
  }
  return { items, total };
}

/** Count state for the picker's footer hint, kept consistent with what the
 *  organizer actually sees (CORE-804 defect 2). `shown` is the count of rows
 *  after the category/search filter; `loaded`/`total` describe the whole
 *  library. When a filter is active the message must lead with `shown`, never
 *  claim «N из M» of the full library beside a short filtered list. */
export interface LibraryCountState {
  truncated: boolean;
  filtered: boolean;
  shown: number;
  loaded: number;
  total: number;
}

export function resolveLibraryCount(
  shown: number,
  loaded: number,
  total: number,
  filterActive: boolean,
): LibraryCountState {
  return {
    truncated: isLibraryTruncated(loaded, total),
    filtered: filterActive,
    shown,
    loaded,
    total,
  };
}

/** The footer hint's text model — an i18n key plus its numbers, so both pickers
 *  (wizard step and the in-room «Сменить медиа» modal) say the same thing and
 *  neither hardcodes Russian. `null` means «render nothing».
 *
 *  CORE-804 QA return 04.09.2026. The hint used to render ONLY while the
 *  library was truncated, which made sense while a picker showed the first 100
 *  of 134 tracks. Once pagination loaded every track (`collectLibraryPages`)
 *  the condition stopped being true and the counter disappeared from every
 *  surface — and a list with no total still can't tell «это всё» from «это
 *  первые N», which is the defect the ticket opened on. So the count is now
 *  always shown for a non-empty library; truncation only changes the wording.
 *
 *  An empty library renders nothing: the pickers already show their own
 *  empty-state, and «Показано 0 из 0» under it is noise, not information. */
export interface LibraryCountLabel {
  i18nKey: string;
  params: { shown: number; loaded: number; total: number };
}

export function libraryCountLabel(state: LibraryCountState): LibraryCountLabel | null {
  if (!Number.isFinite(state.total) || state.total <= 0) return null;
  const params = { shown: state.shown, loaded: state.loaded, total: state.total };
  return {
    // Past LIBRARY_MAX_ITEMS the loaded slice is smaller than the library, so
    // «Показано N из M» of the whole library would be a lie — say both numbers.
    i18nKey: state.truncated ? 'wizard.libraryCount.partial' : 'wizard.libraryCount.shown',
    params,
  };
}
